import React, { useEffect, useState } from "react";
import { MapPin, Wifi, Compass } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import SectionHeading from "@/components/SectionHeading";

export default function GuestGuide() {
  const { t } = useLang();
  const [apartments, setApartments] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/apartments").then((r) => {
      setApartments(r.data);
      if (r.data.length) setSelected(r.data[0]);
    });
  }, []);

  return (
    <div data-testid="guide-page" className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionHeading overline={t("guide.overline")} title={t("guide.title")} subtitle={t("guide.subtitle")} />

        <div className="grid lg:grid-cols-3 gap-12 mt-16">
          <div className="lg:col-span-1">
            <div className="flex flex-col gap-2" data-testid="guide-apartment-list">
              {apartments.map((a) => (
                <button
                  key={a.id}
                  onClick={() => setSelected(a)}
                  data-testid={`guide-select-${a.id}`}
                  className={`text-left px-5 py-4 rounded-sm border transition-all ${
                    selected?.id === a.id ? "bg-petrol text-white border-petrol" : "bg-card border-line text-petrol hover:border-petrol/40"
                  }`}
                >
                  <span className="font-serif text-xl block">{a.name}</span>
                  <span className={`text-xs ${selected?.id === a.id ? "text-white/70" : "text-muted-foreground"}`}>{a.location}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            {selected ? (
              <div className="space-y-8" data-testid="guide-content">
                <div className="aspect-[16/9] rounded-sm overflow-hidden bg-beige">
                  <img src={selected.photos?.[0]} alt={selected.name} className="w-full h-full object-cover" />
                </div>
                <div>
                  <h3 className="font-serif text-3xl text-petrol mb-1">{selected.name}</h3>
                  <p className="flex items-center gap-1.5 text-muted-foreground text-sm"><MapPin size={14} strokeWidth={1.5} /> {selected.map_address || selected.location}</p>
                </div>

                {selected.guest_guide && (
                  <div className="border border-line rounded-sm p-6 bg-card">
                    <p className="overline text-gold mb-3 flex items-center gap-2"><Compass size={14} /> {t("guide.title")}</p>
                    <p className="text-petrol/80 leading-relaxed">{selected.guest_guide}</p>
                  </div>
                )}

                {selected.checkin_instructions && (
                  <div className="border border-line rounded-sm p-6 bg-card">
                    <p className="overline text-gold mb-3">{t("checkin.title")}</p>
                    <p className="text-petrol/80 leading-relaxed">{selected.checkin_instructions}</p>
                  </div>
                )}

                {selected.amenities?.length > 0 && (
                  <div className="border border-line rounded-sm p-6 bg-card">
                    <p className="overline text-gold mb-4">{t("detail.amenities")}</p>
                    <div className="flex flex-wrap gap-2">
                      {selected.amenities.map((a) => (
                        <span key={a} className="text-sm px-3 py-1.5 bg-beige rounded-sm text-petrol">{a}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground">{t("guide.intro")}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
